import { Host, h } from "@stencil/core";
import { forceCSSMinMax } from "../../common/utils";
import { PROGRESS_MAX_VALUE, PROGRESS_MIN_VALUE, PROGRESS_VALUE } from "./constants";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../common/analysis/accessibility";
const ARIA_DESCRIBEDBY = "aria-describedby";
const ARIA_BUSY = "aria-busy";
const SEPARATE_BY_COMMA_REGEX = /\s*,\s*/;
let autoId = 0;
/**
 * The ch-progress is an element that displays the progress status for tasks
 * that take a long time.
 *
 * It implements all accessibility behaviors for determinate and indeterminate
 * progress. It also supports referencing a region to describe its progress.
 *
 * @status experimental
 */
export class ChProgress {
    constructor() {
        this.accessibleName = undefined;
        this.accessibleValueText = undefined;
        this.indeterminate = false;
        this.max = 100;
        this.min = 0;
        this.renderType = "custom";
        this.loadingRegionRef = undefined;
        this.value = 0;
    }
    // Create a unique ID for the control
    #progressId = `ch-progress-${autoId++}`;
    internals;
    el;
    regionLoadingRefChanged(_, oldValue) {
        this.#removeAriaBusyAndAriaDescribedByInRegion(oldValue);
    }
    #getControlId = () => this.el.id ?? this.#progressId;
    #getMaxValue = () => Math.max(this.min, this.max);
    /**
     * The controls doesn't have completed its progress if it is indeterminate or
     * the current value is lesser than the max value.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #isInProgress = () => this.indeterminate ||
        forceCSSMinMax(this.value, this.min, this.max) < this.#getMaxValue();
    #regionHasAriaDescribedByWithProgressId = () => (this.loadingRegionRef.getAttribute(ARIA_DESCRIBEDBY) ?? "").includes(this.#getControlId());
    #setAriaBusyAndAriaDescribedByInRegion = () => {
        if (!this.loadingRegionRef ||
            this.#regionHasAriaDescribedByWithProgressId()) {
            return;
        }
        const controlId = this.#getControlId();
        const ariaDescribedByValue = this.loadingRegionRef.getAttribute(ARIA_DESCRIBEDBY) ?? "";
        this.loadingRegionRef.setAttribute(ARIA_BUSY, "true");
        this.loadingRegionRef.setAttribute(ARIA_DESCRIBEDBY, 
        // Checks if the ariaDescribedBy has a value and appends the ID if
        // necessary
        ariaDescribedByValue === ""
            ? controlId
            : `${ariaDescribedByValue},${controlId}`);
    };
    #removeAriaBusyAndAriaDescribedByInRegion = (regionLoadingRef) => {
        if (!regionLoadingRef) {
            return;
        }
        regionLoadingRef.removeAttribute(ARIA_BUSY);
        if (this.#regionHasAriaDescribedByWithProgressId()) {
            const controlId = this.#getControlId();
            const ariaDescribedByValuesWithoutControlId = regionLoadingRef
                .getAttribute(ARIA_DESCRIBEDBY)
                .split(SEPARATE_BY_COMMA_REGEX)
                .filter(id => id === controlId);
            // If the control ID was the only value for the aria-describedby, there
            // is no need to keep in the DOM the aria-describedby
            if (ariaDescribedByValuesWithoutControlId.length === 0) {
                regionLoadingRef.removeAttribute(ARIA_DESCRIBEDBY);
            }
            // There are more values for the aria-describedby, keep the other values
            else {
                regionLoadingRef.setAttribute(ARIA_DESCRIBEDBY, ariaDescribedByValuesWithoutControlId.join(","));
            }
        }
    };
    #customRender = () => h("slot", null);
    #renderDictionary = {
        custom: this.#customRender
    };
    connectedCallback() {
        // Set the unique ID if it was not already set in the Host
        this.el.id ??= this.#progressId;
        this.el.setAttribute("role", "progressbar");
        const labels = this.internals.labels;
        const accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue
        analyzeLabelExistence(this.el, "ch-progress", labels, accessibleNameFromExternalLabel, this.accessibleName);
    }
    componentWillRender() {
        // We must set the aria-busy="true" and aria-describedby="controlId"
        // attributes in the loadingRegionRef to improve the accessibility
        if (this.#isInProgress()) {
            this.#setAriaBusyAndAriaDescribedByInRegion();
        }
        else {
            this.#removeAriaBusyAndAriaDescribedByInRegion(this.loadingRegionRef);
        }
    }
    disconnectedCallback() {
        this.#removeAriaBusyAndAriaDescribedByInRegion(this.loadingRegionRef);
    }
    render() {
        // TODO: Add a unit test for checking that the progress still works when
        // passing an invalid renderType. It should default to the custom render.
        const progressRender = this.#renderDictionary[this.renderType] ?? this.#customRender;
        const actualMinValue = 
        // aria-valuemin is 0 by default
        this.indeterminate || this.min === 0 ? undefined : `${this.min}`;
        const actualValue = this.indeterminate
            ? undefined
            : `${forceCSSMinMax(this.value, this.min, this.max)}`;
        const actualMaxValue = 
        // aria-valuemax is 100 by default
        this.indeterminate || (this.max === 100 && this.min <= this.max)
            ? undefined
            : `${this.#getMaxValue()}`;
        return (h(Host, { key: 'f651e7aa30911dd7ff8c0e87572524df264ef80f', "aria-label": this.accessibleName, "aria-valuemin": actualMinValue, "aria-valuemax": actualMaxValue, "aria-valuenow": this.accessibleValueText ? undefined : actualValue, "aria-valuetext": this.indeterminate || !this.accessibleValueText
                ? undefined
                : this.accessibleValueText
                    .replace(PROGRESS_MIN_VALUE, actualMinValue)
                    .replace(PROGRESS_MAX_VALUE, actualMaxValue)
                    .replace(PROGRESS_VALUE, actualValue) }, progressRender()));
    }
    static get is() { return "ch-progress"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["progress.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["progress.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "accessibleValueText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "example",
                            "text": "accessibleValueText = `Downloading ${PROGRESS_VALUE} MB of ${PROGRESS_MAX_VALUE} MB`"
                        }],
                    "text": "Assistive technologies often present the `value` as a percentage. If this\nwould not be accurate use this property to make the progress bar value\nunderstandable."
                },
                "attribute": "accessible-value-text",
                "reflect": false
            },
            "indeterminate": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the progress is indeterminate or not. In other words, it\nindicates that an activity is ongoing with no indication of how long it is\nexpected to take.\n\nIf `true`, the `max`, `min` and `value` properties won't be taken into\naccount."
                },
                "attribute": "indeterminate",
                "reflect": true,
                "defaultValue": "false"
            },
            "max": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the maximum value of progress. In other words, how much work the\ntask indicated by the progress component requires.\n\nThis property is not used if indeterminate === true."
                },
                "attribute": "max",
                "reflect": false,
                "defaultValue": "100"
            },
            "min": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the minimum value of progress.\n\nThis property is not used if indeterminate === true."
                },
                "attribute": "min",
                "reflect": false,
                "defaultValue": "0"
            },
            "renderType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"custom\" | string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property specifies how the progress will be render.\n - `\"custom\"`: Useful for making custom renders of the progress. The\n   control doesn't render anything and only projects the content of the\n   default slot. Besides that, all specified properties are still used to\n   implement the control's accessibility."
                },
                "attribute": "render-type",
                "reflect": false,
                "defaultValue": "\"custom\""
            },
            "loadingRegionRef": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLElement | undefined",
                    "resolved": "HTMLElement",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If the control is describing the loading progress of a particular region\nof a page, set this property with the reference of the loading region.\nThis will set the `aria-describedby` and `aria-busy` attributes on the\nloading region to improve the accessibility while the control is in\nprogress.\n\nWhen the control detects that is no longer in progress (aka it is removed\nfrom the DOM or value === maxValue with indeterminate === false), it will\nremove the `aria-busy` attribute and update (or remove if necessary) the\n`aria-describedby` attribute.\n\nIf an ID is set prior to the control's first render, the control will use\nthis ID for the `aria-describedby`. Otherwise, the control will compute a\nunique ID for this matter.\n\n**Important**: If you are using Shadow DOM, take into account that the\n`loadingRegionRef` must be in the same Shadow Tree as this control.\nOtherwise, the `aria-describedby` binding won't work, since the control ID\nis not visible for the `loadingRegionRef`."
                }
            },
            "value": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the current value of the component. In other words, how much of\nthe task that has been completed.\n\nThis property is not used if indeterminate === true."
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "loadingRegionRef",
                "methodName": "regionLoadingRefChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
